/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/prop-types */
import { useState, useEffect, useRef } from "react";
import { ChevronsRight, Image } from "lucide-react";
import ThumbnailSelection from "@/components/ThumbnailSelection/ThumbnailSelection";
import { AssetItem, MediaModel } from "@/database";
import { MediaDetailConfig, MediaEditableConfig } from "@/components";
import { getThumbnailUrlUtil, generateBaseUrl } from "@/utils/mediaUtils";
import { FormikProps } from "formik";

interface MediaThumbnailProps {
  formik: FormikProps<any>;
  currentMediaDetailsForEdit: MediaModel;
  assets: AssetItem[];
  mediaDetailConfig: ReturnType<typeof MediaDetailConfig>;
  mediaEditableConfig: ReturnType<typeof MediaEditableConfig>;
  mediaRatio: string;
  isWizard?: boolean;
  updateModal: any;
  handleTempThumbnailImageUrl: any;
  setThumbSent: (url: string) => void;
  addMediaAsset: any;
  premiereData?: any;
}

const MediaThumbnail: React.FC<MediaThumbnailProps> = ({
  formik,
  currentMediaDetailsForEdit,
  assets,
  mediaDetailConfig,
  mediaEditableConfig,
  mediaRatio,
  isWizard,
  updateModal,
  handleTempThumbnailImageUrl,
  setThumbSent,
  addMediaAsset,
  premiereData,
}) => {
  const isAudio = currentMediaDetailsForEdit?.asset_type === "audio";
  const [showThumb, setShowThumb] = useState<boolean>(true);
  const [assetBaseUrl, setAssetBaseUrl] = useState<string>("");
  const [premierePosterUrl, setPremierePosterUrl] = useState<string>("");
  // Tracks the images object last force-applied via from_url, so a formik
  // reference change alone (e.g. from our own setValues call) doesn't
  // re-trigger the force-update and loop forever.
  const lastForcedImagesRef = useRef<any>(null);

  // // Create object URL for premiere poster file
  // useEffect(() => {
  //   if (premiereData?.posterFile instanceof File) {
  //     const url = URL.createObjectURL(premiereData.posterFile);
  //     setPremierePosterUrl(url);
  //     return () => URL.revokeObjectURL(url);
  //   }
  //   setPremierePosterUrl("");
  // }, [premiereData?.posterFile]);

  // Set assetBaseUrl from assets
  useEffect(() => {
    const thumbAsset = assets?.find((a) => a.name === "thumb");
    const assetBaseUrl = thumbAsset?.baseurl ?? assets?.[0]?.baseurl;
    if (assetBaseUrl) {
      setAssetBaseUrl(assetBaseUrl);
    }
  }, [assets]);

  // Handle thumbnail updates from currentMediaDetailsForEdit
  useEffect(() => {
    // Collect all thumb updates that need to be applied
    const thumbUpdates: Partial<any> = {};
    let hasUpdates = false;
    // When true, the backend has fetched a fresh thumb from an external URL -
    // re-apply every thumb field even if formik already holds a value. Only
    // force once per distinct images object - otherwise the setValues() call
    // below produces a new `formik` reference, which re-runs this effect
    // (formik is a dep) and would force-update again forever.
    const images = currentMediaDetailsForEdit?.images;
    const forceUpdate =
      !!images?.from_url && lastForcedImagesRef.current !== images;

    // Check and prepare poster thumb update
    if (
      (forceUpdate || !formik.values.posterThumbId) &&
      currentMediaDetailsForEdit?.images?.poster
    ) {
      thumbUpdates.posterThumb = getThumbnailUrlUtil({
        media: currentMediaDetailsForEdit,
        id: currentMediaDetailsForEdit?.images?.poster,
        source: currentMediaDetailsForEdit?.source,
      });
      thumbUpdates.posterThumbId = currentMediaDetailsForEdit?.images?.poster;
      hasUpdates = true;
    }

    // Check and prepare landscape thumb update
    if (
      (forceUpdate || !formik.values.landscapeThumbId) &&
      currentMediaDetailsForEdit?.images?.thumb
    ) {
      thumbUpdates.landscapeThumb = getThumbnailUrlUtil({
        media: currentMediaDetailsForEdit,
        id: currentMediaDetailsForEdit?.images?.thumb,
        source: currentMediaDetailsForEdit?.source,
      });
      thumbUpdates.landscapeThumbId = currentMediaDetailsForEdit?.images?.thumb;
      hasUpdates = true;
    }

    // Check and prepare social thumb update
    if (
      (forceUpdate || !formik.values.socialThumbId) &&
      currentMediaDetailsForEdit?.images?.social
    ) {
      thumbUpdates.socialThumb = getThumbnailUrlUtil({
        media: currentMediaDetailsForEdit,
        id: currentMediaDetailsForEdit?.images?.social,
        source: currentMediaDetailsForEdit?.source,
      });
      thumbUpdates.socialThumbId = currentMediaDetailsForEdit?.images?.social;
      hasUpdates = true;
    }

    // Check and prepare portrait thumb update
    if (
      (forceUpdate || !formik.values.portraitThumbId) &&
      currentMediaDetailsForEdit?.images?.portrait
    ) {
      thumbUpdates.portraitThumb = getThumbnailUrlUtil({
        media: currentMediaDetailsForEdit,
        id: currentMediaDetailsForEdit?.images?.portrait,
        source: currentMediaDetailsForEdit?.source,
      });
      thumbUpdates.portraitThumbId =
        currentMediaDetailsForEdit?.images?.portrait;
      hasUpdates = true;
    }

    // Check and prepare podcast thumb update
    if (
      (forceUpdate || !formik.values.podcastThumbId) &&
      currentMediaDetailsForEdit?.images?.podcast
    ) {
      thumbUpdates.podcastThumb = getThumbnailUrlUtil({
        media: currentMediaDetailsForEdit,
        id: currentMediaDetailsForEdit?.images?.podcast,
        source: currentMediaDetailsForEdit?.source,
      });
      thumbUpdates.podcastThumbId = currentMediaDetailsForEdit?.images?.podcast;
      hasUpdates = true;
    }

    // Check and prepare premiere thumb update
    if (
      (forceUpdate || !formik.values.premiereThumbId) &&
      currentMediaDetailsForEdit?.images?.premiere
    ) {
      thumbUpdates.premiereThumb = getThumbnailUrlUtil({
        media: currentMediaDetailsForEdit,
        id: currentMediaDetailsForEdit?.images?.premiere,
        source: currentMediaDetailsForEdit?.source,
      });
      thumbUpdates.premiereThumbId =
        currentMediaDetailsForEdit?.images?.premiere;
      hasUpdates = true;
    }

    // Check and prepare premiere_v thumb update
    if (
      (forceUpdate || !formik.values.premiereVThumbId) &&
      currentMediaDetailsForEdit?.images?.premiere_v
    ) {
      thumbUpdates.premiereVThumb = getThumbnailUrlUtil({
        media: currentMediaDetailsForEdit,
        id: currentMediaDetailsForEdit?.images?.premiere_v,
        source: currentMediaDetailsForEdit?.source,
      });
      thumbUpdates.premiereVThumbId =
        currentMediaDetailsForEdit?.images?.premiere_v;
      hasUpdates = true;
    }

    // Apply all thumb updates in a single setValues call
    if (hasUpdates) {
      formik.setValues({
        ...formik.values,
        ...thumbUpdates,
      });
    }
    if (images?.from_url) {
      lastForcedImagesRef.current = images;
    }

    // Handle media asset addition (separate from thumb updates)
    if (
      currentMediaDetailsForEdit?.images?.list?.[0]?.name ||
      currentMediaDetailsForEdit?.images?.thumb ||
      currentMediaDetailsForEdit?.images?.portrait ||
      currentMediaDetailsForEdit?.images?.podcast ||
      currentMediaDetailsForEdit?.images?.premiere
    ) {
      const assetId =
        currentMediaDetailsForEdit?.images?.list?.[0]?.name ||
        currentMediaDetailsForEdit?.images?.thumb ||
        currentMediaDetailsForEdit?.images?.portrait ||
        currentMediaDetailsForEdit?.images?.podcast ||
        currentMediaDetailsForEdit?.images?.premiere;
      if (!assetId) return;
      const newAsset = {
        baseurl: generateBaseUrl(currentMediaDetailsForEdit),
        ct: currentMediaDetailsForEdit?.ct,
        id: assetId,
        mt: currentMediaDetailsForEdit?.mt,
        name: "thumb",
        nid: 0,
        pid: currentMediaDetailsForEdit?.id,
        status: 3,
        type: "thumb",
        user: "",
      };
      if (assets.length > 0) {
        if (!assets.find((asset) => asset.id === newAsset.id)) {
          addMediaAsset({ asset: newAsset });
        }
      } else {
        addMediaAsset({ asset: newAsset });
      }
    }
  }, [
    currentMediaDetailsForEdit?.images,
    currentMediaDetailsForEdit,
    formik,
    assets,
    addMediaAsset,
  ]);

  // Define all thumbnail configurations
  const allThumbnailConfigs = [
    {
      key: "landscapeThumb",
      enabled: mediaDetailConfig.landscapeThumb.enabled,
      component: (
        <ThumbnailSelection
          key="landscapeThumb"
          thumbType="LANDSCAPE THUMBNAIL"
          assets={assets}
          baseUrl={assetBaseUrl || generateBaseUrl(currentMediaDetailsForEdit)}
          handleThumbnailChange={(newThumbnailUrl: string, id: string) => {
            formik.setFieldValue("landscapeThumb", newThumbnailUrl);
            formik.setFieldValue("landscapeThumbId", id);
            formik.setFieldTouched("landscapeThumbId", false);
            formik.setFieldError("landscapeThumbId", undefined);
            handleTempThumbnailImageUrl(
              currentMediaDetailsForEdit?.images?.base,
              id
            );
            if (currentMediaDetailsForEdit?.is_vertical === 0) {
              setThumbSent(
                getThumbnailUrlUtil({
                  media: currentMediaDetailsForEdit,
                  id,
                  source: currentMediaDetailsForEdit?.source,
                })
              );
            }
          }}
          currentThumbnail={formik.values.landscapeThumb}
          currentThumbnailId={formik.values.landscapeThumbId}
          bottomLabel="Landscape"
          type="thumb"
          disabled={!mediaEditableConfig.landscapeThumb}
          isWizard={isWizard}
          updateModal={updateModal}
          error={formik.errors.landscapeThumbId}
          touched={formik.touched.landscapeThumbId}
          aspectRatio={16 / 9}
          isRequiredThumb={!isAudio}
          currentMediaDetails={currentMediaDetailsForEdit}
        />
      ),
    },
    {
      key: "posterThumb",
      enabled: mediaDetailConfig.posterThumb.enabled,
      component: (
        <ThumbnailSelection
          key="posterThumb"
          thumbType="POSTER THUMBNAIL"
          baseUrl={assetBaseUrl || generateBaseUrl(currentMediaDetailsForEdit)}
          disabled={!mediaEditableConfig.posterThumb}
          assets={assets}
          handleThumbnailChange={(newThumbnailUrl: string, id: string) => {
            formik.setFieldValue("posterThumb", newThumbnailUrl);
            formik.setFieldValue("posterThumbId", id);
            formik.setFieldTouched("posterThumbId", false);
            formik.setFieldError("posterThumbId", undefined);
          }}
          currentThumbnail={formik.values.posterThumb}
          currentThumbnailId={formik.values.posterThumbId}
          bottomLabel="Poster"
          type="poster"
          isWizard={isWizard}
          updateModal={updateModal}
          error={formik.errors.posterThumbId}
          touched={formik.touched.posterThumbId}
          aspectRatio={0}
          isRequiredThumb={true}
          currentMediaDetails={currentMediaDetailsForEdit}
        />
      ),
    },
    {
      key: "portraitThumb",
      enabled: mediaDetailConfig.portraitThumb.enabled,
      component: (
        <ThumbnailSelection
          key="portraitThumb"
          thumbType="PORTRAIT THUMBNAIL"
          assets={assets}
          baseUrl={assetBaseUrl || generateBaseUrl(currentMediaDetailsForEdit)}
          disabled={!mediaEditableConfig.portraitThumb}
          handleThumbnailChange={(newThumbnailUrl: string, id: string) => {
            formik.setFieldValue("portraitThumb", newThumbnailUrl);
            formik.setFieldValue("portraitThumbId", id);
            formik.setFieldTouched("portraitThumbId", false);
            formik.setFieldError("portraitThumbId", undefined);
            if (currentMediaDetailsForEdit?.is_vertical === 1) {
              setThumbSent(
                getThumbnailUrlUtil({
                  media: currentMediaDetailsForEdit,
                  id,
                  source: currentMediaDetailsForEdit?.source,
                })
              );
            }
          }}
          currentThumbnail={formik.values.portraitThumb}
          currentThumbnailId={formik.values.portraitThumbId}
          bottomLabel="Portrait"
          type="portrait"
          updateModal={updateModal}
          isWizard={isWizard}
          error={formik.errors.portraitThumbId}
          touched={formik.touched.portraitThumbId}
          aspectRatio={9 / 16}
          isRequiredThumb={!isAudio}
          currentMediaDetails={currentMediaDetailsForEdit}
        />
      ),
    },
    {
      key: "socialThumb",
      enabled: mediaDetailConfig.socialThumb.enabled,
      component: (
        <ThumbnailSelection
          key="socialThumb"
          thumbType="SOCIAL THUMBNAIL"
          baseUrl={assetBaseUrl || generateBaseUrl(currentMediaDetailsForEdit)}
          disabled={!mediaEditableConfig.socialThumb}
          assets={assets}
          handleThumbnailChange={(newThumbnailUrl: string, id: string) => {
            formik.setFieldValue("socialThumb", newThumbnailUrl);
            formik.setFieldValue("socialThumbId", id);
            formik.setFieldTouched("socialThumbId", false);
            formik.setFieldError("socialThumbId", undefined);
            formik.setFieldValue("is_social_change", true);
          }}
          currentThumbnail={formik.values.socialThumb}
          currentThumbnailId={formik.values.socialThumbId}
          bottomLabel="Social"
          type="social"
          isWizard={isWizard}
          updateModal={updateModal}
          error={formik.errors.socialThumbId}
          touched={formik.touched.socialThumbId}
          aspectRatio={16 / 9}
          isRequiredThumb={true}
          currentMediaDetails={currentMediaDetailsForEdit}
        />
      ),
    },
    {
      key: "podcastThumb",
      enabled: mediaDetailConfig.podcastThumb.enabled,
      component: (
        <ThumbnailSelection
          key="podcastThumb"
          thumbType="PODCAST THUMBNAIL"
          baseUrl={assetBaseUrl || generateBaseUrl(currentMediaDetailsForEdit)}
          disabled={!mediaEditableConfig.podcastThumb}
          assets={assets}
          handleThumbnailChange={(newThumbnailUrl: string, id: string) => {
            formik.setFieldValue("podcastThumb", newThumbnailUrl);
            formik.setFieldValue("podcastThumbId", id);
            formik.setFieldTouched("podcastThumbId", false);
            formik.setFieldError("podcastThumbId", undefined);
          }}
          currentThumbnail={formik.values.podcastThumb}
          currentThumbnailId={formik.values.podcastThumbId}
          bottomLabel="Podcast"
          type="podcast"
          isWizard={isWizard}
          updateModal={updateModal}
          error={formik.errors.podcastThumbId}
          touched={formik.touched.podcastThumbId}
          aspectRatio={1 / 1}
          isRequiredThumb={!isAudio}
          currentMediaDetails={currentMediaDetailsForEdit}
        />
      ),
    },
    {
      key: "premiereThumb",
      enabled:
        mediaDetailConfig.premiereThumb.enabled &&
        (currentMediaDetailsForEdit?.media_ref?.rel === "premiere" ||
          currentMediaDetailsForEdit?.media_ref?.rel === "premiere_v"),
      component: (
        <ThumbnailSelection
          key="premiereThumb"
          thumbType="PREMIERE THUMBNAIL"
          baseUrl={assetBaseUrl || generateBaseUrl(currentMediaDetailsForEdit)}
          disabled={!mediaEditableConfig.premiereThumb}
          assets={assets}
          handleThumbnailChange={(newThumbnailUrl: string, id: string) => {
            formik.setFieldValue("premiereThumb", newThumbnailUrl);
            formik.setFieldValue("premiereThumbId", id);
            formik.setFieldTouched("premiereThumbId", false);
            formik.setFieldError("premiereThumbId", undefined);
            if (
              currentMediaDetailsForEdit?.media_ref?.rel === "premiere" ||
              currentMediaDetailsForEdit?.media_ref?.rel === "premiere_v"
            ) {
              setThumbSent(newThumbnailUrl);
            }
          }}
          currentThumbnail={formik.values.premiereThumb}
          currentThumbnailId={formik.values.premiereThumbId}
          bottomLabel="Premiere"
          type="premiere"
          isWizard={isWizard}
          updateModal={updateModal}
          error={formik.errors.premiereThumbId}
          touched={formik.touched.premiereThumbId}
          aspectRatio={16 / 9}
          isRequiredThumb={!isAudio}
          currentMediaDetails={currentMediaDetailsForEdit}
        />
      ),
    },
    // {
    //   key: "premiereVThumb",
    //   enabled:
    //     mediaDetailConfig.premiereVThumb.enabled &&
    //     (currentMediaDetailsForEdit?.media_ref?.rel === "premiere" ||
    //       currentMediaDetailsForEdit?.media_ref?.rel === "premiere_v"),
    //   component: (
    //     <ThumbnailSelection
    //       key="premiereVThumb"
    //       thumbType="PREMIERE V THUMBNAIL"
    //       baseUrl={assetBaseUrl || generateBaseUrl(currentMediaDetailsForEdit)}
    //       disabled={!mediaEditableConfig.premiereVThumb}
    //       assets={assets}
    //       handleThumbnailChange={(newThumbnailUrl: string, id: string) => {
    //         formik.setFieldValue("premiereVThumb", newThumbnailUrl);
    //         formik.setFieldValue("premiereVThumbId", id);
    //         formik.setFieldTouched("premiereVThumbId", false);
    //         formik.setFieldError("premiereVThumbId", undefined);
    //       }}
    //       currentThumbnail={formik.values.premiereVThumb}
    //       currentThumbnailId={formik.values.premiereVThumbId}
    //       bottomLabel="Premiere V"
    //       type="premiere_v"
    //       isWizard={isWizard}
    //       updateModal={updateModal}
    //       error={formik.errors.premiereVThumbId}
    //       touched={formik.touched.premiereVThumbId}
    //       aspectRatio={9 / 16}
    //       isRequiredThumb={false}
    //       currentMediaDetails={currentMediaDetailsForEdit}
    //     />
    //   ),
    // },
  ];

  // Function to determine thumbnail order based on conditions
  const getThumbnailOrder = () => {
    // Get all enabled thumbnails
    const enabledThumbnails = allThumbnailConfigs.filter(
      (config) => config.enabled
    );

    if (
      currentMediaDetailsForEdit?.media_ref?.rel === "podcast" ||
      currentMediaDetailsForEdit?.asset_type === "audio"
    ) {
      return enabledThumbnails.sort((a, b) => {
        if (a.key === "podcastThumb") return -1;
        if (b.key === "podcastThumb") return 1;
        if (a.key === "landscapeThumb") return -1;
        if (b.key === "landscapeThumb") return 1;
        if (a.key === "posterThumb") return -1;
        if (b.key === "posterThumb") return 1;
        return 0;
      });
    } else if (
      currentMediaDetailsForEdit?.media_ref?.rel === "premiere" ||
      currentMediaDetailsForEdit?.media_ref?.rel === "premiere_v"
    ) {
      return enabledThumbnails.sort((a, b) => {
        const order = [
          "premiereThumb",
          "premiereVThumb",
          "landscapeThumb",
          "posterThumb",
        ];
        const aIndex = order.indexOf(a.key);
        const bIndex = order.indexOf(b.key);
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return 0;
      });
    } else if (mediaRatio === "portrait") {
      return enabledThumbnails.sort((a, b) => {
        if (a.key === "portraitThumb") return -1;
        if (b.key === "portraitThumb") return 1;
        if (a.key === "posterThumb") return -1;
        if (b.key === "posterThumb") return 1;
        if (a.key === "landscapeThumb") return -1;
        if (b.key === "landscapeThumb") return 1;
        return 0;
      });
    }

    return enabledThumbnails.sort((a, b) => {
      const order = [
        "landscapeThumb",
        "posterThumb",
        "portraitThumb",
        "socialThumb",
        "podcastThumb",
        "premiereThumb",
        "premiereVThumb",
      ];
      const aIndex = order.indexOf(a.key);
      const bIndex = order.indexOf(b.key);
      return aIndex - bIndex;
    });
  };

  // Get thumbnails in the determined order (ALL enabled thumbnails)
  const orderedThumbnails = getThumbnailOrder();

  // Filter thumbnails based on showThumb condition AFTER ordering
  const visibleThumbnails = orderedThumbnails.filter((config) => {
    // Always show podcast thumbnail for podcast content
    if (mediaRatio === "podcast") {
      if (config.key === "landscapeThumb" || config.key === "podcastThumb") {
        return true;
      }
    } else if (mediaRatio === "landscape") {
      if (config.key === "landscapeThumb" || config.key === "posterThumb") {
        return true;
      }
    } else if (mediaRatio === "portrait") {
      if (config.key === "portraitThumb" || config.key === "posterThumb") {
        return true;
      }
    } else if (mediaRatio === "premiere") {
      if (
        config.key === "premiereThumb" ||
        config.key === "premiereVThumb" ||
        config.key === "posterThumb"
      ) {
        return true;
      }
    } else if (
      config.key === "landscapeThumb" ||
      config.key === "posterThumb"
    ) {
      return true;
    }
    // Show other thumbnails only when showThumb is true
    return showThumb === true;
  });

  return (
    <div className={`${isWizard ? "g-3" : "gap-3"} row m-0`}>
      <h6 className="mt-3 p-0">
        Thumbnail {!isAudio && <span className="required ms-1">*</span>}
      </h6>
      {/* Premiere poster placeholder — always first for premiere flows */}
      {premiereData && (
        <div className={`${isWizard ? "col-2" : "col-2 p-0"}`}>
          <div
            style={{ width: "100%" }}
            className="ratio ratio-16x9 position-relative p-0 bg-white border rounded"
          >
            <div className="d-flex gap-2 bg-light">
              <div
                style={{
                  height: "100%",
                  maxWidth: "100%",
                  minWidth: "100%",
                  position: "relative",
                }}
                className="rounded-top d-flex justify-content-center align-items-center"
              >
                {premierePosterUrl ? (
                  <img
                    src={premierePosterUrl}
                    height="100%"
                    style={{ objectFit: "contain", maxWidth: "100%" }}
                    alt="Premiere Poster"
                    className="rounded-top"
                  />
                ) : (
                  <div className="fallback-image text-secondary">
                    <Image size={28} />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="bg-dark-subtle p-2">Premiere</div>
        </div>
      )}

      {/* Dynamic thumbnail rendering with conditional ordering */}
      {visibleThumbnails.map((config) => config.component)}

      {/* Show more thumbnails button */}
      {showThumb === false && (
        <div className={`${isWizard ? "col-3" : "col-2 p-0"}`}>
          <div
            className="thumb-placeholder text-secondary"
            style={{ width: "100%", height: "100%" }}
            onClick={() => setShowThumb(true)}
          >
            Show More <ChevronsRight />
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaThumbnail;
